import { TourPlacement } from './placement';

/**
 * Page tours. Each step points at an element marked `data-tour="<target>"`.
 * The home and sign-in pages have no tour, so no Explore button.
 * Steps whose element isn't on screen (another tab, a role-only control, an empty state)
 * are skipped, so one tour can describe every variant of a page.
 *
 * A competition can be used as a participant, a judge or a host. Its pages have one tour
 * per role, and the page picks the one that matches the viewer (see useTourRole).
 */

/** Roles are per competition: you can join one, judge another and host a third. */
export type TourRole = 'participant' | 'judge' | 'host';

export interface TourStep {
  /** `data-tour` value of the element to highlight. No target: a centered card. */
  target?: string;
  title: string;
  body: string;
  placement?: TourPlacement;
  /** Only in these roles' tours. Omitted: in every role's tour. */
  roles?: TourRole[];
}

export interface PageTour {
  id: string;
  /** Route patterns, as in App.tsx. */
  paths: string[];
  name: string;
  /** Roles this page has a tour for. Omitted: one tour for everyone. */
  roles?: TourRole[];
  /** The tour shown when the page hasn't reported the viewer's role (e.g. still loading). */
  defaultRole?: TourRole;
  /** First card of each role's tour. */
  intros?: Partial<Record<TourRole, Omit<TourStep, 'target' | 'roles'>>>;
  steps: TourStep[];
}

const FOOTER: TourStep = {
  target: 'site-footer',
  title: 'Help and policies',
  body: 'The privacy policy, terms of service and the support email are always at the bottom of the page.',
};

const TOUR_BUTTON: TourStep = {
  target: 'tour-button',
  title: 'Explore any page',
  body: 'Every page has its own tour. Select Explore whenever you want a walkthrough of the page you are on.',
};

const ACCOUNT_NAV: TourStep = {
  target: 'account-nav',
  title: 'Your account',
  body: 'Switch between your overview, notifications, competitions and settings.',
};

const P: TourRole[] = ['participant'];
const J: TourRole[] = ['judge'];
const H: TourRole[] = ['host'];

export const TOURS: PageTour[] = [
  {
    id: 'dashboard',
    paths: ['/dashboard'],
    name: 'Competitions',
    steps: [
      { title: 'Welcome to your dashboard', body: 'This tour shows where to find competitions and how to get around. Use the buttons or the arrow keys to move, and press Esc to leave at any time.' },
      { target: 'your-competitions', title: 'Your competitions', body: 'Competitions you joined, judge or host, each with a shortcut to the next thing to do: Submit, Evaluate or Manage. See all opens the full list.' },
      { target: 'list-controls', title: 'Search and filter', body: 'Search by title or description, then narrow the list by status (Open, New, Closed, Archived) or submission type.' },
      { target: 'competition-grid', title: 'Competition cards', body: 'Each card shows the status, submission type, visibility, how many have entered and a $$ marker for money prizes. Open a card to read the details and join. You can join New competitions before they open.' },
      { target: 'main-nav', title: 'Main menu', body: 'Competitions lists everything you can join. My competitions collects what you joined, judge and host. Admins also see Admin.' },
      { target: 'bottom-nav', title: 'Main menu', body: 'Switch between competitions, your own competitions, alerts and your profile from this bar.' },
      { target: 'notifications', title: 'Notifications', body: 'The bell shows how many unread notifications you have, such as judging assignments, deadline reminders and messages from hosts.' },
      { target: 'account-menu', title: 'Your account', body: 'Open your profile and settings, or sign out. Sign out when you use a shared computer.' },
      TOUR_BUTTON,
    ],
  },
  {
    id: 'competition',
    paths: ['/competition/:id'],
    name: 'Competition',
    roles: ['participant', 'judge', 'host'],
    defaultRole: 'participant',
    intros: {
      participant: { title: 'Taking part', body: 'Read the overview, join, then submit from the Submit tab before the deadline. This tour walks through each part.' },
      judge: { title: 'Judging this competition', body: 'Read what entrants were asked to do, then open the judging page with Evaluate.' },
      host: { title: 'Hosting this competition', body: 'This is what participants see. Check it reads the way you want, then make changes from Manage.' },
    },
    steps: [
      { target: 'error-card', title: 'Competition unavailable', body: 'This competition doesn\'t exist or you don\'t have access to it. If you expected to see it, ask its host to check your access.' },
      { target: 'competition-header', title: 'Competition at a glance', body: 'The title, logo and sponsor of this competition.' },
      { target: 'competition-pills', title: 'Status and format', body: 'Status, submission type, individual or team mode, prize and visibility. Hover over or tap any label for a short explanation.' },
      { target: 'competition-action', roles: P, title: 'Join', body: 'Join adds you as a competitor. If the competition is New, you can join now and submit once it opens.' },
      { target: 'competition-action', roles: J, title: 'Evaluate', body: 'Judges see Evaluate here. It opens the judging page for this competition.' },
      { target: 'competition-action', roles: H, title: 'Manage', body: 'Hosts see Manage here. It opens the page where you edit content, set up judging and handle people.' },
      { target: 'competition-deadline', roles: ['participant', 'host'], title: 'Deadline', body: 'How long until submissions close and how many people or teams have entered.' },
      { target: 'countdown', title: 'Countdown', body: 'A countdown the hosts turned on. It is for display only: the competition closes when its status changes to Closed.' },
      { target: 'competition-tabs', roles: P, title: 'Sections', body: 'Overview has the details and instructions, Submit is where you upload your work, and Leaderboard shows the rankings when they are visible.' },
      { target: 'competition-tabs', roles: J, title: 'Sections', body: 'Overview has the brief and instructions entrants worked from. Leaderboard shows the rankings when they are visible.' },
      { target: 'competition-tabs', roles: H, title: 'Sections', body: 'Hosts can open Submit to preview it the way participants see it, without run controls. Open another tab and select Explore again to learn about it.' },
      { target: 'competition-overview', roles: P, title: 'Details and instructions', body: 'Read the description, what to submit, the timeline and the full instructions before you start. Use Expand to read long instructions.' },
      { target: 'competition-overview', roles: J, title: 'Know the brief', body: 'Read what entrants were asked to submit, so you judge against the brief rather than your own idea of it.' },
      { target: 'competition-overview', roles: H, title: 'What participants read', body: 'Check the description and instructions. Edit them from Manage, Content.' },
      { target: 'competition-submit-status', roles: ['participant', 'host'], title: 'Can you submit?', body: 'Explains why submitting isn\'t available right now, for example the competition hasn\'t opened, it has closed, or only your team lead can submit.' },
      { target: 'submit-form', roles: P, title: 'Make a submission', body: 'Upload a .zip, paste a link or fill in the project form, depending on the competition type. On a .zip, check the run label first: a practice run doesn\'t use one of your graded submissions.' },
      { target: 'submit-form', roles: H, title: 'Submission form preview', body: 'The form participants fill in. As a host you see it read-only.' },
      { target: 'run-logs', roles: P, title: 'Run logs', body: 'Download the logs from your latest run to troubleshoot it. Available once a run has finished.' },
      { target: 'live-terminals', roles: P, title: 'Live runs', body: 'Follow your run here: queued, starting, running, finishing and the result. Practice runs stream live terminal output; graded runs show progress only.' },
      { target: 'practice-history', roles: P, title: 'Practice history', body: 'Your past practice runs and their scores per task. Practice scores are a guide; graded results are the official ones.' },
      { target: 'competition-leaderboard', roles: P, title: 'Leaderboard', body: 'The current rankings. A live board can still change until the competition ends; a final board shows only the finalists.' },
      { target: 'competition-leaderboard', roles: J, title: 'Leaderboard', body: 'Rankings that come partly from judges\' scores and votes, when the hosts make them visible.' },
      { target: 'competition-leaderboard', roles: H, title: 'Leaderboard', body: 'What participants see. Choose whether it is visible in Manage, Evaluation.' },
    ],
  },
  {
    id: 'evaluations',
    paths: ['/evaluations/:id'],
    name: 'Judging',
    steps: [
      { title: 'Judging entries', body: 'Read the instructions, review each entry completely, then score or vote. Every evaluation you submit counts toward the results. Hosts can open this page to see exactly what judges see.' },
      { target: 'eval-hero', title: 'What is being judged', body: 'The competition being judged. See competition opens its page.' },
      { target: 'eval-allowance', title: 'Evaluations left', body: 'How many evaluations you still have and how far along you are.' },
      { target: 'eval-instructions-toggle', title: 'Judging instructions', body: 'Open the host\'s instructions or rubric and read them before you score.' },
      { target: 'eval-guidance', title: 'How this page works', body: 'A short summary of what to do on this page.' },
      { target: 'eval-pair', title: 'Compare two entries', body: 'Review both submissions completely before you decide. For some video competitions, voting unlocks after a short wait.' },
      { target: 'eval-project', title: 'Project under review', body: 'The project being scored, with its video, slides, description and other files.' },
      { target: 'eval-project-empty', title: 'No project open yet', body: 'Pick a project from Available submissions to review it. If the list is empty, projects appear once participants submit; you can\'t judge your own project or team.' },
      { target: 'eval-picker', title: 'Available submissions', body: 'Choose the next project to review. Projects you already scored are marked so you can skip them.' },
      { target: 'eval-tracks', title: 'Filter by track', body: 'Projects are entered in tracks. Pick a track to see only its projects, for example if you were asked to judge one track. The project view shows each project\'s track too.' },
      { target: 'eval-score', title: 'Score it', body: 'Score each rubric criterion from 1 to 5, or give one overall score from 1 to 100, add feedback if needed, then submit.' },
      { target: 'eval-feedback', title: 'Feedback', body: 'Write specific, constructive feedback. When the host requires it, you need at least 10 characters before you can vote.' },
      { target: 'eval-vote', title: 'Vote', body: 'Pick the stronger entry, or Draw if they are equal. After you vote, load the next pair.' },
      { target: 'eval-empty', title: 'Nothing left to judge', body: 'Either your evaluations are used up or there is no eligible work left for you. Contact the host if that looks wrong.' },
    ],
  },
  {
    id: 'submission',
    paths: ['/submission/:id'],
    name: 'Submission',
    steps: [
      { title: 'Your submission', body: 'Check what you submitted and how judges will see it.' },
      { target: 'submission-header', title: 'Your latest submission', body: 'Which version this is and how many submissions the competition allows.' },
      { target: 'submission-consent', title: 'Showcase consent', body: 'Choose whether your entry may appear in a public finalist showcase.' },
      { target: 'submission-instructions', title: 'How judges will see it', body: 'This view shows your latest submission the way it will appear during judging.' },
      { target: 'submission-view', title: 'What you submitted', body: 'Your file, link or project package as it was submitted.' },
      { target: 'submission-empty', title: 'No submission yet', body: 'There is nothing to show yet. Submit from the competition page first.' },
    ],
  },
  {
    id: 'present',
    paths: ['/manage/:id/present'],
    name: 'Present mode',
    steps: [
      { title: 'Present mode', body: 'A read-only walkthrough of submissions for live demos, classes and finalist presentations. Nothing here records a vote or a score.' },
      { target: 'present-select', title: 'Choose a submission', body: 'Pick any participant or team from the list. Entries without a submission are marked.' },
      { target: 'present-nav', title: 'Move through entries', body: 'Use Prev and Next, or the left and right arrow keys.' },
      { target: 'present-reload', title: 'Reload', body: 'Fetch the current submission again if it was just updated.' },
      { target: 'present-names', title: 'Hide names', body: 'Hide names in the bar and the heading for blind reviews. Names inside the submitted files can still appear.' },
      { target: 'present-fullscreen', title: 'Full screen', body: 'Fill the whole display for your presentation.' },
      { target: 'present-stage', title: 'The submission', body: 'The selected entry. Project competitions split it into Video, Slides, Description and Details tabs.' },
    ],
  },
  {
    id: 'manage',
    paths: ['/manage/:id'],
    name: 'Manage competition',
    steps: [
      { title: 'Running a competition', body: 'Set up what participants read, how judging works and who takes part. Work through the tabs from left to right before you open submissions.' },
      { target: 'breadcrumbs', title: 'Find your way back', body: 'Jump back to the dashboard or the competition page. Save your changes first: these links don\'t always warn you about unsaved edits.' },
      { target: 'manage-summary', title: 'Competition summary', body: 'Status, type, mode and dates at a glance. The You label shows your role, which decides what you can change.' },
      { target: 'manage-tabs', title: 'Management tabs', body: 'Overview checks readiness, Content edits what participants see, Evaluation sets up judging, and People handles access, teams and messages. Open another tab and select Explore again to learn about it.' },
      { target: 'manage-present', title: 'Present mode', body: 'Opens a full-screen walkthrough of submissions in a new tab, for live reviews. There is no voting in Present mode.' },
      { target: 'manage-stats', title: 'Readiness numbers', body: 'Participants or teams, how many have submitted, how many judges you have, and evaluations done versus capacity.' },
      { target: 'manage-shortcuts', title: 'Shortcuts', body: 'Jump straight to Content, Evaluation or People to fix anything that is missing.' },
      { target: 'manage-details', title: 'Details', body: 'Title, description, what to submit, status, visibility, prize, mode, dates and submission quota. Read-only fields show the role needed to change them. Save each section before you leave it.' },
      { target: 'manage-countdown', title: 'Countdown', body: 'An optional banner with a closing time. It is for display only: submissions stay open until the status is set to Closed.' },
      { target: 'manage-logo', title: 'Logo', body: 'The image shown on competition cards and the competition page.' },
      { target: 'manage-markdown-readme', title: 'Instructions', body: 'The Readme participants see on the competition page, written in Markdown.' },
      { target: 'manage-markdown-evaluation', title: 'Judge instructions', body: 'Guidance shown to judges in the instructions panel on the judging page.' },
      { target: 'manage-eval-settings', title: 'Judging settings', body: 'Leaderboard visibility, whether judges must write feedback, and how many rounds each pair is shown in pairwise judging.' },
      { target: 'manage-rubric', title: 'Rubric', body: 'For project competitions: criteria with 1 to 5 guidance and weights, or a single 1 to 100 score. Avoid changing it once judging has started.' },
      { target: 'manage-judges', title: 'Judges', body: 'How many evaluations each judge may do. Set it per judge or for everyone at once.' },
      { target: 'manage-judging-actions', title: 'Judging and results', body: 'Try the judging page yourself, see evaluation results, or run auto evaluation for code and CSV competitions.' },
      { target: 'manage-danger-zone', title: 'Danger zone', body: 'Reset judging or delete a submission. Both are permanent, so double-check the competition first.' },
      { target: 'manage-people', title: 'People and access', body: 'See who has access, add competitors, judges or viewers, add hosts, send notifications and manage teams. Some actions need host admin.' },
    ],
  },
  {
    id: 'profile',
    paths: ['/profile'],
    name: 'Profile overview',
    steps: [
      ACCOUNT_NAV,
      { target: 'account-stats', title: 'Your stats', body: 'Competitions joined and won, total submissions and your best rank.' },
      { target: 'account-todo', title: 'Needs your attention', body: 'Unread notifications, evaluations you still owe as a judge, and competitions you joined that close soon. Each links straight there.' },
    ],
  },
  {
    id: 'profile-work',
    paths: ['/profile/work'],
    name: 'My competitions',
    steps: [
      ACCOUNT_NAV,
      { target: 'work-tabs', title: 'Joined, Judging, Hosting', body: 'Competitions you entered, judge and host. The numbers show how many are in each.' },
      { target: 'list-controls', title: 'Search and filter', body: 'Search and filter by status or type. Filters apply to the tab you are on.' },
      { target: 'work-list', title: 'Your competitions', body: 'Open a card to submit, judge or manage, depending on the tab.' },
    ],
  },
  {
    id: 'notifications',
    paths: ['/profile/notifications'],
    name: 'Notifications',
    steps: [
      { target: 'notif-filters', title: 'Filter your inbox', body: 'Show all, unread or completed notifications, or refresh the list.' },
      { target: 'notif-bulk', title: 'Tidy up', body: 'Mark everything as read, or clear read and completed items. Unread items and unfinished tasks are always kept.' },
      { target: 'notif-list', title: 'Your notifications', body: 'Each one shows who sent it, the competition and any due date. Use its buttons to open the related page, mark it read or completed, or delete it. Judging assignments arrive here as tasks.' },
      { target: 'notif-empty', title: 'All caught up', body: 'Nothing matches this filter right now.' },
    ],
  },
  {
    id: 'settings',
    paths: ['/profile/settings'],
    name: 'Settings',
    steps: [
      { target: 'settings-profile', title: 'Profile details', body: 'Your username, email and name. Edit changes your nickname and name.' },
      { target: 'settings-security', title: 'Security', body: 'Change your iKompete password if you sign in with a password.' },
      { target: 'settings-appearance', title: 'Appearance', body: 'Choose Light, Dark or Match system for the whole site.' },
    ],
  },
  {
    id: 'admin',
    paths: ['/admin'],
    name: 'Admin',
    steps: [
      { title: 'Platform admin', body: 'Create competitions, generate judging pairs and manage users. Only platform admins can open this area.' },
      { target: 'admin-nav', title: 'Admin sections', body: 'Competitions, Evaluations and Users.' },
      { target: 'page-actions', title: 'Section actions', body: 'Actions for this section, such as creating a competition, adding a host, sending a notification or adding users.' },
      { target: 'admin-competitions', title: 'Manage a competition', body: 'Pick any competition and jump straight to its management page.' },
      { target: 'admin-evaluations', title: 'PairPool', body: 'Generate and inspect unique judging pairs for pairwise competitions. Generate them after submissions arrive and before judges start.' },
      { target: 'admin-users', title: 'User directory', body: 'Search users by username or name and page through the list. Search before adding someone, to avoid duplicates.' },
    ],
  },
  {
    id: 'public-competition',
    paths: ['/public/competition/:id'],
    name: 'Public competition',
    steps: [
      { target: 'public-nav', title: 'Public showcase page', body: 'A read-only view of a published competition. You can\'t submit from here; sign in to take part in live competitions.' },
      { target: 'public-hero', title: 'The competition', body: 'Its title and logo.' },
      { target: 'public-meta', title: 'Details', body: 'Description, what was submitted, dates, type, mode and prize.' },
      { target: 'public-instructions', title: 'Instructions', body: 'The instructions entrants worked from.' },
      { target: 'public-leaderboard', title: 'Leaderboard', body: 'The rankings, when the hosts made them public.' },
      { target: 'error-card', title: 'Not available', body: 'This competition isn\'t published for public viewing.' },
      FOOTER,
    ],
  },
  {
    id: 'privacy',
    paths: ['/privacy'],
    name: 'Privacy policy',
    steps: [
      { target: 'legal-header', title: 'Privacy policy', body: 'How iKompete uses your account data, submissions, votes and logs to run competitions.' },
      { target: 'legal-content', title: 'The details', body: 'Scroll through each section. Questions go to the support email in the footer.' },
      FOOTER,
    ],
  },
  {
    id: 'terms',
    paths: ['/terms'],
    name: 'Terms of service',
    steps: [
      { target: 'legal-header', title: 'Terms of service', body: 'The rules for using iKompete.' },
      { target: 'legal-content', title: 'The details', body: 'Scroll through each section. Questions go to the support email in the footer.' },
      FOOTER,
    ],
  },
  {
    id: 'support',
    paths: ['/support'],
    name: 'Support',
    steps: [
      { target: 'legal-header', title: 'Support', body: 'Where to get help with iKompete.' },
      { target: 'legal-content', title: 'Contact DSRS', body: 'Email us about access, competition issues or anything else. Include the competition name, what you were doing and any message you saw.' },
      FOOTER,
    ],
  },
  {
    id: 'sso-callback',
    paths: ['/sso-callback'],
    name: 'Signing in',
    steps: [
      { title: 'Finishing your sign-in', body: 'iKompete is completing your Illinois sign-in. You will be taken to your dashboard in a moment. If nothing happens, go back to the sign-in page and try again.' },
    ],
  },
  {
    id: 'message',
    paths: ['/message', '/unauthorized', '/verify', '/reset'],
    name: 'Message',
    steps: [
      { target: 'message-card', title: 'Status message', body: 'The result of what you just did, such as verifying your email or opening a page you don\'t have access to.' },
      { target: 'message-actions', title: 'Continue', body: 'Close the message to carry on. Some messages close by themselves after a few seconds.' },
    ],
  },
];

/** The steps for the viewer: their role's intro and steps, or the page's default role when it isn't known. */
export function stepsForRole(tour: PageTour, role: TourRole | null): TourStep[] {
  if (!tour.roles) return tour.steps;
  const chosen = role && tour.roles.includes(role) ? role : (tour.defaultRole || tour.roles[0]);
  const intro = tour.intros?.[chosen];
  const steps = tour.steps.filter((step) => !step.roles || step.roles.includes(chosen));
  return intro ? [intro, ...steps] : steps;
}

const segments = (path: string) => path.split('/').filter(Boolean);

/** Whole-path match of a route pattern such as `/manage/:id` (the patterns here only use `:param` segments). */
export function matchesRoute(pattern: string, pathname: string): boolean {
  const want = segments(pattern);
  const have = segments(pathname);
  return want.length === have.length && want.every((part, i) => part.startsWith(':') || part === have[i]);
}

/** The tour for a URL path, or null when no tour matches. */
export function findTour(pathname: string): PageTour | null {
  return TOURS.find((tour) => tour.paths.some((path) => matchesRoute(path, pathname))) || null;
}
